<template>
  <AppPopup v-model="open">
    <!-- Loading -->
    <div v-if="loading" class="min-w-96 h-[660px] flex items-center justify-center">
      <div class="h-12 w-12">
        <SpinnerBounce />
      </div>
    </div>

    <!-- Content -->
    <div v-else>
      <div class="p-5">
        <Vue3Lottie
          animation-link="https://lottie.host/797182c1-f740-4caf-9c8d-3eed7ab40594/JQKX1EqdmR.json"
          :height="100"
          :width="100"
          :loop="false"
          :auto-play="true"
        />

        <div class="mt-4 mb-4 text-gray-500 text-sm flex flex-col items-center justify-center gap-1">
          <div>Amount</div>
        </div>
        <div class="relative flex items-center justify-center">
          <div class="w-full flex items-center justify-center">
            <input ref="amount_ref" v-model="amount" @focus="isFocused = true" @blur="isFocused = false" />
          </div>
          <div
            class="absolute w-full h-20 bg-white hover:text-gray-600 text-gray-700"
            :class="[formatted_amount.length > 10 ? 'overflow-x-scroll overflow-y-hidden' : 'flex items-center justify-center -ml-5']"
            @click="amount_ref.focus()"
          >
            <div class="text-6xl text-center font-bold placeholder:text-gray-700 border-none focus:outline-none focus:ring-0">
              {{ formatted_amount }}<span v-if="isFocused" class="cursor-fake bg-gray-500"></span>
            </div>
          </div>
        </div>

        <div class="h-4 w-full mt-6 mb-5">
          <Transition
            enter-active-class="transition-all duration-200 ease-in-out"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in-out"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
          >
            <div
              v-if="!from_account.is_external"
              :class="[sufficient_funds ? 'text-gray-500' : 'text-orange-600 font-semibold', 'text-xs text-center']"
            >
              (Available to withdraw {{ fromAccountBalance }})
            </div>
          </Transition>
        </div>

        <div class="w-80">
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-2">
              <div class="text-gray-500 max-w-80">Select an account to transfer from</div>
              <AppSelect v-model="from_account" class="w-full" :items="accounts" type="accounts-with-ids" label="Funds Coming From" />
            </div>

            <div v-if="from_account" class="flex flex-col gap-2">
              <div class="flex my-8">
                <div class="w-full border-t border-dashed border-gray-300"></div>
              </div>

              <div class="text-gray-500 max-w-80">Select an account to transfer to</div>
              <AppSelect v-model="to_account" class="w-full" :items="to_accounts" type="accounts-with-ids" :inverted="true" label="Funds Going To" />
            </div>

            <div class="h-16 w-full mt-2">
              <Transition
                enter-active-class="transition-all duration-200 ease-in-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in-out"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="from_account.is_external == false">
                  <div v-if="from_account.is_external == false && from_account.has_linked_external_account == false" class="flex flex-col gap-2">
                    <div class="text-yellow-500 max-w-80 text-xs">This account does not have a linked external account.</div>
                    <div class="text-blue-600 hover:text-blue-400 text-sm flex gap-1 items-center cursor-pointer w-fit" @click="handleLinkClick()">
                      <LinkIcon class="size-4" />
                      <div>Link an external account</div>
                    </div>
                  </div>
                  <div v-else class="flex flex-col gap-2">
                    <div class="text-gray-500 max-w-80 text-xs">
                      Need to update you external account? Each account can have only one external account connected at a time.
                    </div>
                    <div class="text-blue-600 hover:text-blue-400 text-sm flex gap-1 items-center cursor-pointer w-fit" @click="handleLinkClick()">
                      <CloudArrowUpIcon class="size-5" />
                      <div>Update external account</div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
          <div class="flex justify-between gap-5 mt-8">
            <AppButtonSecondary text="Cancel" size="lg" @click="open = false" />
            <AppButtonPrimary text="Transfer" size="lg" :disabled="!ready_to_transfer" :loading="transfering" @click="transfer()" />
          </div>
        </div>
      </div>
    </div>
  </AppPopup>
</template>

<script setup>
// Essentials
import { ref, watch, watchEffect, computed, onMounted } from "vue";
// import { computedAsync } from "@vueuse/core";

// Icons
import { Vue3Lottie } from "vue3-lottie";
import { CloudArrowUpIcon, LinkIcon } from "@heroicons/vue/24/outline";

// Components
import SpinnerBounce from "@/components/loading/SpinnerBounce.vue";
import AppPopup from "@/components/applicationui/AppPopup.vue";
import AppSelect from "@/components/applicationinputs/AppSelect.vue";
import AppButtonPrimary from "@/components/applicationui/AppButtonPrimary.vue";
import AppButtonSecondary from "@/components/applicationui/AppButtonSecondary.vue";

// Helpers
import { dollarize, undollarize } from "@/helper/format.js";

// Stores
import { useAccountsStore } from "@/stores/accounts.js";
const accountsStore = useAccountsStore();
import { useTransactionsStore } from "@/stores/transactions.js";
const transactionsStore = useTransactionsStore();
import { usePartnerStore } from "@/stores/partner.js";
const partnersStore = usePartnerStore();

// Model
const open = defineModel({
  type: Boolean,
  required: true,
});

// Emits
const emit = defineEmits(["transfer", "error", "link"]);

// Handle Link Click START
const handleLinkClick = () => {
  open.value = false;
  emit("link", from_account.value);
};
// Handle Link Click END

// Get Accounts START
const accounts = ref([]);
const getAccounts = async () => {
  // Get Custody Accounts
  const custody_accounts = await accountsStore.getCustodyAccounts();
  custody_accounts.forEach((account) => {
    accounts.value.push({
      ...account,
      is_external: false,
    });

    if (account.has_linked_external_account) {
      // eslint-disable-next-line no-unused-vars
      const { name, tapi_account_id, ...rest } = account;
      accounts.value.unshift({
        ...rest,
        tapi_account_id: "Ext. Account",
        name: account.external_account_nickname || "External Account",
        is_external: true,
      });
    }
  });

  // Set Default From Account
  from_account.value = accounts.value[0];

  if (to_accounts.value.length > 0) {
    to_account.value = to_accounts.value[0];
  }
};
const to_accounts = computed(() => {
  return accounts.value.filter((account) => {
    if (from_account.value.is_external) {
      if (account.id === from_account.value.id && account.tapi_account_id !== from_account.value.tapi_account_id) {
        return account;
      }
    } else {
      if (account.id === from_account.value.id && account.tapi_account_id !== from_account.value.tapi_account_id) {
        return account;
      }
      if (!account.is_external && account.tapi_account_id !== from_account.value.tapi_account_id) {
        return account;
      }
    }
  });
});
// Get Accounts END

// Transfer Funds START
const from_account = ref(null);
const fromAccountBalance = ref("$0.00");
watchEffect(async () => {
  // Watch effects runs whenever a dependency changes ie, from_account
  const accountId = from_account.value?.id;
  if (!accountId) {
    fromAccountBalance.value = "No account selected";
    return;
  }

  try {
    const balance = await accountsStore.getAccountAvailableCashBalance(accountId);
    fromAccountBalance.value = dollarize(balance, balance < 100);
  } catch (error) {
    console.error("Error fetching balance:", error);
    fromAccountBalance.value = "$0.00";
  }
});
const to_account = ref(null);
const amount_ref = ref(null);
const isFocused = ref(false);
const amount = ref("0");
const formatted_amount = computed(() => {
  return dollarize(amount.value, amount.value.includes("."));
});
const sufficient_funds = computed(() => {
  if (!from_account.value.is_external && parseFloat(amount.value) > undollarize(fromAccountBalance.value)) {
    return false;
  }
  return true;
});
const ready_to_transfer = computed(() => {
  if (!from_account.value || !to_account.value) return false;
  if (!sufficient_funds.value) return false;
  if (parseFloat(amount.value) <= 0) return false;
  if (from_account.value.id === to_account.value.id && from_account.value.tapi_account_id === to_account.value.tapi_account_id) return false;
  return true;
});

const transfering = ref(false);
const transfer = async () => {
  transfering.value = true;
  if (from_account.value.is_external) {
    await deposit();
  }
  else if (to_account.value.is_external) {
    await withdraw();
  } else {
    await internalTransfer();
  }
};
// Transfer Funds START

// Withdrawal START
const withdraw = async () => {
  const response = await accountsStore.createCustodyAccountWithdrawal(
    from_account.value.id,
    from_account.value.tapi_account_id,
    amount.value,
    from_account.value.name,
    partnersStore.partner.title,
  );
  if (!response) {
    transfering.value = false;
    open.value = false;
    emit("error");
    return;
  }

  const success = await transactionsStore.createTransactionInSupabase(
    null, // No associated TAPI trade ID
    from_account.value.id,
    null, // No associated Offering
    "cash",
    1,
    partnersStore.partner.id,
    amount.value,
    "withdraw",
    "ach",
  );
  if (!success) {
    transfering.value = false;
    open.value = false;
    emit("error");
    return;
  }

  transfering.value = false;
  open.value = false;
  emit("transfer");
};
// Withdrawal END

// Deposit START
const deposit = async () => {
  const response = await accountsStore.createCustodyAccountDeposit(
    to_account.value.id,
    to_account.value.tapi_account_id,
    amount.value,
    to_account.value.name,
    partnersStore.partner.title,
  );
  if (!response) {
    transfering.value = false;
    open.value = false;
    emit("error");
    return;
  }

  const success = await transactionsStore.createTransactionInSupabase(
    null, // No associated TAPI trade ID
    to_account.value.id,
    null, // No associated Offering
    "cash",
    1,
    partnersStore.partner.id,
    amount.value,
    "deposit",
    "ach",
  );
  if (!success) {
    transfering.value = false;
    open.value = false;
    emit("error");
    return;
  }

  transfering.value = false;
  open.value = false;
  emit("transfer");
};
// Deposit END

// Internal Transfer START
const internalTransfer = async () => {
  // Create withdrawal transaction from the from_account
  const successful_withdrawal = await transactionsStore.createTransactionInSupabase(
    null, // No associated TAPI trade ID
    from_account.value.id,
    null, // No associated Offering
    "cash",
    1,
    partnersStore.partner.id,
    amount.value,
    "withdraw",
    "tbd",
  );

  if (!successful_withdrawal) {
    transfering.value = false;
    open.value = false;
    emit("error");
    return;
  }

  // Create withdrawal transaction from the from_account
  const successful_deposit = await transactionsStore.createTransactionInSupabase(
    null, // No associated TAPI trade ID
    to_account.value.id,
    null, // No associated Offering
    "cash",
    1,
    partnersStore.partner.id,
    amount.value,
    "deposit",
    "tbd",
  );

  if (!successful_deposit) {
    transfering.value = false;
    open.value = false;
    emit("error");
    return;
  }

  transfering.value = false;
  open.value = false;
  emit("transfer");
};
// Internal Transfer END

// Mounted
const loading = ref(true);
onMounted(async () => {
  await getAccounts();
  // Ready
  loading.value = false;
});

// Amount Watcher
watch(amount, (new_amount) => {
  let cleaned_amount = new_amount.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

  if (cleaned_amount.includes(".") && cleaned_amount.split(".")[1].length > 2) {
    const last_char = cleaned_amount.slice(-1);
    cleaned_amount = cleaned_amount.slice(0, -2) + last_char;
  }

  amount.value = cleaned_amount;
});

watch(from_account, () => {
  if (to_accounts.value.length > 0) {
    to_account.value = to_accounts.value[0];
  }
});
</script>

<style>
.cursor-fake {
  display: inline-block;
  width: 2px;
  margin-left: 1px;
  margin-bottom: -1px;
  height: 45px;
  animation: blink 0.9s steps(1) infinite;
}

@keyframes blink {
  0% {
    visibility: visible;
  }
  50% {
    visibility: hidden;
  }
  100% {
    visibility: visible;
  }
}
</style>
